import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useGlobalStore } from '../../../application/store/store';
import isEqual from 'fast-deep-equal';
import { Drug } from '../../../application/types/drug';
import { getDrugDisplayName } from '../../../common/utils/getDrugDisplayName.ts';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import StorageService from '../../storageManagement/services/StorageService.ts';
import { axiosInstance } from '../../../common/axiosInstans.ts';

interface AvailableDrugsProps {
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    handleSelect: (drug: Drug) => void;
    className?: string | undefined;
    filterOut?: Drug[];
    storageId?: string | undefined;
    allTenantDrugs?: boolean;
}

const storageService = new StorageService();
const AvailableDrugs: React.FC<AvailableDrugsProps> = ({
    searchTerm,
    setSearchTerm,
    handleSelect,
    className,
    filterOut,
    storageId,
    allTenantDrugs = false,
}) => {
    const availableDrugs = useGlobalStore((state) => state.availableDrugs);
    const setAvailableDrugs = useGlobalStore(
        (state) => state.setAvailableDrugs
    );

    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );

    const selectedDrug = useGlobalStore((state) => state.selectedDrug);

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const setMessage = useGlobalStore((state) => state.setMessage);
    const selectedStorage = useGlobalStore((state) => state.selectedStorage);

    const memoizedAvailableDrugs = useMemo(
        () => availableDrugs,
        [availableDrugs]
    );

    const fetchAvailableDrugs = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            if (allTenantDrugs) {
                const allDrugsResp = await axiosInstance.get('/drug');
                const allDrugs = allDrugsResp.data;
                if (!isEqual(allDrugs, memoizedAvailableDrugs)) {
                    setAvailableDrugs(allDrugs);
                }
            } else {
                if (storageId != null) {
                    const storageInventory =
                        await storageService.getStorageInventory(storageId);
                    const inventoryData = storageInventory.data.inventory;
                    let drugList: Drug[] = [];
                    if (Array.isArray(inventoryData)) {
                        inventoryData.forEach((item) => {
                            if (item.drug) {
                                drugList.push(item.drug as Drug);
                            }
                        });
                    }
                    const uniqueDrugs: Drug[] = Array.from(
                        new Map<string, Drug>(
                            drugList.map((d) => [d.id, d])
                        ).values()
                    );
                    if (!isEqual(uniqueDrugs, memoizedAvailableDrugs)) {
                        setAvailableDrugs(uniqueDrugs);
                    }
                    if (drugList.length <= 0) {
                        setMessage(
                            'Inga unika mediciner med gränsvärden hittades.',
                            'info',
                            10000
                        );
                    }
                } else if (selectedStorage?.id) {
                    const response = await axiosInstance.get(
                        `/storagelocation/${selectedStorage.id}/inventory`
                    );
                    let drugsData = response.data;

                    // Extract the inventory array from the response object
                    if (
                        drugsData &&
                        drugsData.inventory &&
                        Array.isArray(drugsData.inventory)
                    ) {
                        const drugList: Drug[] = drugsData.inventory
                            .map((item) => item.drug as Drug)
                            .filter(Boolean) as Drug[];
                        const uniqueDrugs: Drug[] = Array.from(
                            new Map<string, Drug>(
                                drugList.map((d) => [d.id, d])
                            ).values()
                        );
                        if (!isEqual(uniqueDrugs, memoizedAvailableDrugs)) {
                            setAvailableDrugs(uniqueDrugs);
                        }
                    } else {
                        // If the structure is unexpected, set empty array
                        console.warn(
                            'Unexpected API response structure:',
                            drugsData
                        );
                        setAvailableDrugs([]);
                    }
                }
            }
        } catch (error) {
            console.error('Fel vid hämtning av läkemedel:', error);
            setError('Det gick inte att hämta läkemedel');
            setAvailableDrugs([]);
        } finally {
            setIsLoading(false);
        }
    }, [
        setAvailableDrugs,
        memoizedAvailableDrugs,
        storageId,
        selectedStorage?.id,
        setMessage,
        allTenantDrugs,
    ]);

    useEffect(() => {
        fetchAvailableDrugs();
    }, [fetchAvailableDrugs]);

    useEffect(() => {
        if (selectedDrug) {
            const formattedDrugValue = getDrugDisplayName(selectedDrug);
            setSearchTerm(formattedDrugValue);
        }
    }, [selectedDrug, setSearchTerm]);

    const handleFocus = () => {
        if (availableDrugs.length <= 0) {
            setMessage(
                'Inga unika mediciner med gränsvärden hittades.',
                'info',
                10000
            );
        }
        setIsOpen(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsOpen(false);
        }
    };

    const filteredDrugs = useMemo(() => {
        // Safety check - ensure we're always working with an array
        const availableDrugsArray = Array.isArray(memoizedAvailableDrugs)
            ? memoizedAvailableDrugs
            : [];

        const filtered = searchTerm
            ? availableDrugsArray.filter(
                  (drug) =>
                      drug.drugName
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) &&
                      !filterOut?.some((item) => item.id === drug.id)
              )
            : availableDrugsArray.filter(
                  (drug) => !filterOut?.some((item) => item.id === drug.id)
              );

        // Sort drugs alphabetically by drugName
        return filtered.sort((a, b) => a.drugName.localeCompare(b.drugName));
    }, [memoizedAvailableDrugs, searchTerm, filterOut]);

    const handleSelectDrug = (drug: Drug) => {
        const formattedDrugValue = getDrugDisplayName(drug);
        setSearchTerm(formattedDrugValue);
        handleSelect(drug);
        setIsOpen(false);
    };

    const handleClear = () => {
        resetSelectedDrug();
        setSearchTerm('');
    };

    return (
        <Box sx={{ width: '100%', textAlign: 'center', px: { xs: 0, sm: 0 } }}>
            {error && <p className="text-red-500">{error}</p>}

            <div className="relative w-full" onBlur={handleBlur}>
                <TextField
                    id="drug-search"
                    name="drugSearch"
                    type="text"
                    autoComplete="off"
                    placeholder="Sök läkemedel"
                    className={className}
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={handleFocus}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls="drug-list"
                    aria-label="Sök läkemedel"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchTerm && (
                                    <IconButton
                                        aria-label="clear search"
                                        onClick={handleClear}
                                        edge="end"
                                        size="small"
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                )}
                                <IconButton
                                    aria-label="toggle dropdown"
                                    edge="end"
                                    size="small"
                                    onClick={() => {
                                        if (!isOpen && searchTerm) {
                                            // Clear search term to show all options
                                            setSearchTerm('');
                                        }
                                        setIsOpen(!isOpen);
                                    }}
                                >
                                    <ArrowDropDownIcon
                                        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {isOpen && (
                    <div
                        id="drug-list"
                        role="listbox"
                        className="absolute z-10 mt-1 left-0 right-0 bg-white shadow-lg rounded-lg max-h-40 overflow-auto border border-gray-300"
                    >
                        {isLoading ? (
                            <p className="p-4 text-center">Laddar...</p>
                        ) : filteredDrugs.length > 0 ? (
                            filteredDrugs.map((drug) => (
                                <div
                                    key={drug.id}
                                    onMouseDown={() => handleSelectDrug(drug)}
                                    role="option"
                                    className="p-2 hover:bg-gray-100 cursor-pointer text-sm w-full text-left"
                                    aria-selected={selectedDrug?.id === drug.id}
                                >
                                    {getDrugDisplayName(drug)}
                                </div>
                            ))
                        ) : (
                            <p className="p-4 text-center">
                                Inga läkemedel funna.
                                <br />
                                Lägg till gränsvärden för att lista mediciner.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Box>
    );
};

export default AvailableDrugs;
